import { useState, useEffect, useRef } from 'react';
import { extractApiErrorDetail } from '@/lib/api-error';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Card } from '@/components/ui/card';
import fetchWithAuth, { pauseNegativeKeywords } from '@/lib/apis';
import { apiEndpoint } from '@/lib/consts';
import { useToast } from '@/hooks/useToast';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { useLargeListWarning } from '@/hooks/useLargeListWarning';
import { useNegativeKeywordsReportQuery } from '@/lib/query-options';
import { X, ExternalLink } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Ad } from '@/lib/types';



interface NegativeKeywordsReportData {
  search_terms: SearchTerm[];
  ads: Array<Ad & { negative_keywords?: NegativeKeyword[]; googleads_ad_group_id?: string }>;
}

// Search Terms (rows in search terms table)
interface SearchTerm {
  search_term: string;
  googleads_ad_group_id: string;
  impressions: number;
  clicks: number;
  conversions: number;
  cost: number;
  ctr: number;
  cvr: number;
  is_negative_candidate: boolean;
  is_already_negative?: boolean;
  matched_rules: Array<{
    confidence_level: string;
    priority: number | string;
    tags: string[];
    reason: string;
    campaign_type: string;
  }>;
  rule_results?: Record<string, { reason: string }>;
}


// Interface for negative keywords
interface NegativeKeyword {
  googleads_ad_group_id: string;
  text: string;
  keyword?: string; // Keep for backward compatibility
  match_type?: string;
  is_paused?: boolean;
}



/* NegativeKeywordsManager Component */
export const NegativeKeywordsManager = ({ campaignSlug }: { campaignSlug?: string }) => {
  // Use React Query for data fetching with caching
  const { data: reportData, isLoading, error: queryError } = useNegativeKeywordsReportQuery(campaignSlug || '', 7);
  const error = queryError?.message || null;
  const queryClient = useQueryClient();
  
  const searchTerms = reportData?.search_terms || [];
  // Guard (#65): this search-terms table renders every row un-virtualized; warn in dev on big accounts.
  useLargeListWarning(searchTerms.length, 'NegativeKeywordsManager search terms');
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState<NegativeKeyword | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [isAddingAllTerms, setIsAddingAllTerms] = useState<boolean>(false);
  const [selectedAdId, setSelectedAdId] = useState<number | null>(null);
  const [selectedAdGroupId, setSelectedAdGroupId] = useState<string | null>(null);
  // Extract values from API response  
  const campaignAverages = reportData?.campaign_averages || null;
  const numericCampaignId = reportData?.id || null;
  const campaignAds = reportData?.ads || [];
  const [selectedTerms, setSelectedTerms] = useState<Set<string>>(new Set());
  const [inputValue, setInputValue] = useState('');
  // Action types and targets removed per requirements
  const [isExactMatch, setIsExactMatch] = useState<boolean>(true);
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);
  const { toast } = useToast();
  const { symbol: currencySymbol } = useCurrencySymbol('google');
  
  // Format date inline using Intl.DateTimeFormat for day with ordinal suffix
  const formatDate = (dateString?: string): string => 
    dateString ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(dateString)) : 'unknown date';
  
  // Helper functions for managing selections
  const toggleTermSelection = (term: SearchTerm) => {
    // Skip if the term is already a negative keyword
    if (term.is_already_negative) {
      return;
    }
    
    const termKey = `${term.search_term}-${term.googleads_ad_group_id}`;
    const newSelected = new Set(selectedTerms);
    newSelected.has(termKey) ? newSelected.delete(termKey) : newSelected.add(termKey); // Toggle. 
    setSelectedTerms(newSelected);
    
    // When any individual row changes, uncheck the "select all" checkbox
    setSelectAllChecked(false);
  };

  // Function to toggle all terms selection
  const toggleAllTerms = (checked: boolean) => {
    const filteredTerms = applyFilters(searchTerms);
    const newSelected = new Set(selectedTerms);
    
    if (checked) {
      // Add all filtered terms to selection (excluding already-negative terms)
      filteredTerms.forEach(term => {
        if (!term.is_already_negative) {
          const termKey = `${term.search_term}-${term.googleads_ad_group_id}`;
          newSelected.add(termKey);
        }
      });
    } else {
      // Remove all filtered terms from selection
      filteredTerms.forEach(term => {
        const termKey = `${term.search_term}-${term.googleads_ad_group_id}`;
        newSelected.delete(termKey);
      });
    }
    
    setSelectedTerms(newSelected);
    setSelectAllChecked(checked);
  };

  // Checks for selection
  const isTermSelected = (term: SearchTerm): boolean => {
    const termKey = `${term.search_term}-${term.googleads_ad_group_id}`;
    return selectedTerms.has(termKey);
  };

  // Apply filters for ad and ad group
  const applyFilters = (terms: SearchTerm[]) => {
    if (!terms || !Array.isArray(terms)) {
      return [];
    }

    return terms.filter(term => {
      // If no filters are active, return all terms
      if (selectedAdId === null && selectedAdGroupId === null) {
        return true;
      }

      // If ad ID filter is active
      if (selectedAdId !== null && term.googleads_ad_group_id) {
        // Find the ad in campaignAds
        const ad = (campaignAds as Ad[]).find((ad: Ad) => ad.id === selectedAdId);
        // If the ad exists and has an ad group ID, filter by it
        if (ad && 'googleads_ad_group_id' in ad) {
          // Compare as strings to ensure consistent comparison
          const termAdGroupId = String(term.googleads_ad_group_id);
          const adAdGroupId = String((ad as Ad & { googleads_ad_group_id?: string }).googleads_ad_group_id);

          // If the term's ad group ID matches the ad's ad group ID, include it
          if (termAdGroupId === adAdGroupId) {
            return true;
          }
          return false;
        }
      }

      // If ad group ID filter is active
      if (selectedAdGroupId !== null && term.googleads_ad_group_id) {
        // Compare as strings
        return String(term.googleads_ad_group_id) === String(selectedAdGroupId);
      }

      return true;
    });
  };



  

  // Populate Add modal when it shows. 
  useEffect(() => {
    if(!showAddModal)
    {
      // console.log("     * Add modal not showing.")
      return
    }

    // console.log("  * Add modal is showing.")

    // Create a helper function to format terms with exact match and include ad_group_id
    const formatTerm = (term: string, adGroupId: string) => {
      const cleanTerm = term.replace(/^\[|\]$/g, '');
      const formattedTerm = isExactMatch ? `[${cleanTerm}]` : cleanTerm;
      return `${formattedTerm} in ${adGroupId}\n`;
    };

    if (isAddingAllTerms) {
      // console.log("  * Adding *all* terms.", searchTerms)
      const selectedSearchTerms = searchTerms.filter((term: SearchTerm) => {
        const termKey = `${term.search_term}-${term.googleads_ad_group_id}`;
        return selectedTerms.has(termKey);
      });
     
      // Format each term with its googleads_ad_group_id and join with newlines
      const formattedTerms = selectedSearchTerms.map((term: SearchTerm) => 
        formatTerm(term.search_term.trim(), term.googleads_ad_group_id)
      );
      
      setInputValue(formattedTerms.join(''));

    } else if (selectedKeyword) { // For individual terms
      // console.log("  * Adding a specific term.", selectedKeyword.keyword)
      const keywordText = selectedKeyword.text || selectedKeyword.keyword || '';
      setInputValue(formatTerm(keywordText, selectedKeyword.googleads_ad_group_id));

    } else {
      // console.log("  * ERROR.")
      setInputValue('');
    }
  }, [selectedKeyword, isExactMatch, showAddModal, isAddingAllTerms, selectedTerms, searchTerms]);
  

  // Initialize data when reportData changes
  useEffect(() => {
    if (reportData?.search_terms) {
      const searchTermsData = reportData.search_terms;
      
      // Reset selected filters when data changes
      setSelectedAdId(null);
      setSelectedAdGroupId(null);
      
      // Initialize selected terms with negative candidates that aren't already negative keywords
      const initialSelectedTerms = new Set<string>();
      searchTermsData.forEach((term: SearchTerm) => {
        // Only add to selected terms if it's a negative candidate AND not already a negative keyword
        if (term.is_negative_candidate && !term.is_already_negative) {
          const termKey = `${term.search_term}-${term.googleads_ad_group_id}`;
          initialSelectedTerms.add(termKey);
        }
      });
      setSelectedTerms(initialSelectedTerms);
    }
  }, [reportData]);
  
  
  // Get negative keyword candidates count and list
  const negativeCandidates = searchTerms ? searchTerms.filter((term: SearchTerm) => term.is_negative_candidate) : [];
  const candidatesCount = negativeCandidates.length;
  
  // Function to show modal for applying selected or all negative keyword candidates
  const handleApplyAllCandidates = () => {
    // Check if we have selected terms or should fall back to candidates
    const hasSelectedTerms = selectedTerms.size > 0;
    const hasTermsToProcess = hasSelectedTerms || candidatesCount > 0;
    
    if (!campaignSlug || !hasTermsToProcess || campaignAds.length === 0) {
      toast({
        title: "Cannot Apply Keywords",
        description: !hasTermsToProcess
          ? "No negative keywords selected or found" 
          : "Campaign information is not available",
        variant: "destructive"
      });
      return;
    }
  
    setIsAddingAllTerms(true); // Set flag to indicate we're adding multiple terms
    setSelectedKeyword(null); // Clear selected keyword since we're adding multiple terms
    setShowAddModal(true);  // Show the modal
  };
  

  // Function to process all negative keyword candidates or selected terms
  const processAllNegativeKeywords = async () => {
    setIsApplyingAll(true);
    
    try {
      // Validate critical requirements first
      if (!numericCampaignId) {
        toast({
          title: "Cannot Add Keywords",
          description: "Campaign information is not available",
          variant: "destructive"
        });
        return;
      }

      // Handle single keyword case from "+ Add" button
      if (!isAddingAllTerms && selectedKeyword) {
        const entry: NegativeKeyword = {
          googleads_ad_group_id: selectedKeyword.googleads_ad_group_id,
          text: selectedKeyword.keyword || '',
          keyword: selectedKeyword.keyword,
          match_type: isExactMatch ? "EXACT" : "BROAD"
        };
        
        // console.log("  * Sending single keyword", entry);
        // console.log("  * Using numericCampaignId for request:", numericCampaignId);
        
        const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${numericCampaignId}/negative-keywords`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify([entry]), // Send as array with single entry
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(extractApiErrorDetail(errorData.detail) || 'Failed to add negative keyword');
        }
        
        // Update React Query cache data
        queryClient.setQueryData(['negative-keywords-report', campaignSlug, 7], (oldData: NegativeKeywordsReportData | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            search_terms: oldData.search_terms.map((term: SearchTerm) => {
              if (term.googleads_ad_group_id === entry.googleads_ad_group_id &&
                  term.search_term === entry.keyword) {
                return { ...term, is_already_negative: true };
              }
              return term;
            }),
            ads: oldData.ads.map((ad) => {
              if (ad.googleads_ad_group_id === entry.googleads_ad_group_id) {
                const newNegativeKeywords = [...(ad.negative_keywords || [])];
                newNegativeKeywords.push({
                  googleads_ad_group_id: entry.googleads_ad_group_id,
                  text: entry.text || entry.keyword || '',
                  keyword: entry.keyword || entry.text,
                  match_type: entry.match_type
                });
                return { ...ad, negative_keywords: newNegativeKeywords };
              }
              return ad;
            })
          };
        });
        
        toast({
          title: "Negative Keyword Added",
          description: `Added "${selectedKeyword.keyword}" as a negative keyword`,
          variant: "default"
        });
        
      } else {
        // Handle multiple keywords case from "Add selected" button
        // Only work with selected terms
        const hasSelectedTerms = selectedTerms.size > 0;
        
        if (!campaignSlug || !hasSelectedTerms || campaignAds.length === 0) {
          toast({
            title: "Cannot Add Keywords",
            description: !hasSelectedTerms 
              ? "No negative keywords selected" 
              : "Campaign information is not available",
            variant: "destructive"
          });
          return;
        }
        
        // Get all selected search terms
        const selectedSearchTerms = searchTerms.filter((term: SearchTerm) => {
          const termKey = `${term.search_term}-${term.googleads_ad_group_id}`;
          return selectedTerms.has(termKey);
        });
        
        // Prepare entries for the API call using the ad_group_id directly as a string
        const entries: NegativeKeyword[] = selectedSearchTerms.map((term: SearchTerm) => ({
          googleads_ad_group_id: term.googleads_ad_group_id, // Use the raw string instead of parseInt
          keyword: term.search_term,
          match_type: isExactMatch ? "EXACT" : "BROAD"
        })).filter((entry: NegativeKeyword) => entry.googleads_ad_group_id); // Filter out any without valid googleads_ad_group_id
        
        // console.log ("  * Sending multiple keywords", entries)
        
        // Make a single API call with all entries
        if (entries.length > 0) {
          const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${numericCampaignId}/negative-keywords`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(entries), // Send the entries array directly
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(extractApiErrorDetail(errorData.detail) || 'Failed to add negative keywords');
          }
          
          // Update React Query cache data
          queryClient.setQueryData(['negative-keywords-report', campaignSlug, 7], (oldData: NegativeKeywordsReportData | undefined) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              search_terms: oldData.search_terms.map((term: SearchTerm) => {
                const wasAdded = entries.some(entry =>
                  entry.googleads_ad_group_id === term.googleads_ad_group_id &&
                  entry.keyword === term.search_term
                );
                if (wasAdded) {
                  return { ...term, is_already_negative: true };
                }
                return term;
              }),
              ads: oldData.ads.map((ad) => {
                const adEntries = entries.filter(entry => entry.googleads_ad_group_id === ad.googleads_ad_group_id);
                if (adEntries.length > 0) {
                  const newNegativeKeywords = [...(ad.negative_keywords || [])];
                  adEntries.forEach(entry => {
                    newNegativeKeywords.push({
                      googleads_ad_group_id: entry.googleads_ad_group_id,
                      text: entry.text || entry.keyword || '',
                      keyword: entry.keyword || entry.text,
                      match_type: entry.match_type
                    });
                  });
                  return { ...ad, negative_keywords: newNegativeKeywords };
                }
                return ad;
              })
            };
          });
          
          // Show success message
          const uniqueKeywordCount = new Set(selectedSearchTerms.map((term: SearchTerm) => term.search_term)).size;
          const uniqueAdGroupCount = new Set(selectedSearchTerms
            .filter((term: SearchTerm) => term.googleads_ad_group_id)
            .map((term: SearchTerm) => term.googleads_ad_group_id)
          ).size;
          
          toast({
            title: "Negative Keywords Added",
            description: `Added ${uniqueKeywordCount} keywords to ${uniqueAdGroupCount} ad group${uniqueAdGroupCount > 1 ? 's' : ''}`,
            variant: "default"
          });
          
          // Clear selected terms
          setSelectedTerms(new Set());
        }
      }
      
    } catch (error) {
      // console.error('Error applying negative keywords:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : 'Failed to apply negative keywords',
        variant: "destructive"
      });
      
      // Fall back to a full refresh in case of error
      // Data will automatically refresh via React Query
    } finally {
      setIsApplyingAll(false);
      setIsAddingAllTerms(false);
      setShowAddModal(false);
    }
  };
  
  // Refresh functionality is now handled by React Query automatically

  // Build the flat, ordered list of search-term rows once (candidates first, then the rest),
  // mirroring the section ordering the table renders. This drives the virtualizer below.
  const filteredTerms = applyFilters(searchTerms);
  const sortedTerms = [...filteredTerms].sort((a, b) => {
    // Most critical check: compare already-negative status first
    if (a.is_already_negative !== b.is_already_negative) {
      return a.is_already_negative ? 1 : -1; // Already-negative goes to bottom
    }

    // If both are NOT already-negative, sort by candidate status
    if (!a.is_already_negative) {
      if (a.is_negative_candidate !== b.is_negative_candidate) {
        return a.is_negative_candidate ? -1 : 1; // LLM flagged goes to top
      }
      // If both are candidates, sort by cost (higher first)
      if (a.is_negative_candidate) {
        return b.cost - a.cost;
      }
    }

    return 0;
  });
  const displayTerms = [
    ...sortedTerms.filter(term => term.is_negative_candidate),
    ...sortedTerms.filter(term => !term.is_negative_candidate),
  ];

  // Virtualize the search-terms table: only the visible window of rows is mounted, with
  // top/bottom spacer rows preserving scroll height and column layout (#5309).
  const termsScrollRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: displayTerms.length,
    getScrollElement: () => termsScrollRef.current,
    estimateSize: () => 52,
    overscan: 12,
  });

  // Single row renderer shared by every virtual item (previously duplicated per section).
  const renderTermRow = (term: SearchTerm, flatIndex: number) => {
    let rowClass = '';
    if (term.is_already_negative) {
      rowClass = 'bg-gray-100 text-gray-500'; // Grey out already-negative terms
    } else if (term.is_negative_candidate) {
      rowClass = 'bg-red-50';
    }

    return (
      <TableRow
        key={`${term.search_term}-${term.googleads_ad_group_id}-${flatIndex}`}
        data-index={flatIndex}
        ref={rowVirtualizer.measureElement}
        className={`${rowClass} ${term.is_already_negative ? '' : 'cursor-pointer'}`}
        onClick={() => !term.is_already_negative && toggleTermSelection(term)}
        title={term.matched_rules.length > 0
          ? `Reason: ${term.matched_rules[0].reason}`
          : term.is_already_negative
            ? 'This search term is already a negative keyword'
            : ''}
      >
        <TableCell className="font-medium border-r border-gray-200">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={isTermSelected(term)}
              onChange={(e) => {
                e.stopPropagation();
                toggleTermSelection(term);
              }}
              disabled={term.is_already_negative}
              className={`h-4 w-4 mr-2 ${term.is_already_negative ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600'} border-gray-300 rounded`}
              style={{ width: '16px', height: '16px', minWidth: '16px', minHeight: '16px' }}
              title={term.is_already_negative ? "This term is already a negative keyword" : term.is_negative_candidate ? "Negative Keyword Candidate" : "Select this keyword"}
            />
            {term.search_term}
          </div>
        </TableCell>
        <TableCell className="text-right">{term.impressions.toLocaleString()}</TableCell>
        <TableCell className="text-right">{term.clicks.toLocaleString()}</TableCell>
        <TableCell className="text-right">{term.conversions.toLocaleString()}</TableCell>
        <TableCell className="text-right border-l border-gray-200">{currencySymbol}{term.cost.toFixed(2)}</TableCell>
        <TableCell className="text-right">{term.clicks > 0 ? `${currencySymbol}${(term.cost / term.clicks).toFixed(2)}` : '-'}</TableCell>
        <TableCell className="text-right">{(term.ctr * 100).toFixed(2)}%</TableCell>
        <TableCell className="text-right">{(term.cvr * 100).toFixed(2)}%</TableCell>
        <TableCell className="text-sm hidden">
          <div className="flex flex-col gap-1">
            {/* Only show the Ad button if we have a matching ad */}
            {(() => {
              const matchingAd = (campaignAds as Array<Ad & { googleads_ad_group_id?: string }>).find((ad) => ad.googleads_ad_group_id && String(ad.googleads_ad_group_id) === term.googleads_ad_group_id);
              if (matchingAd) {
                return (
                  <button
                    className="text-left text-xs px-2 py-1 hover:bg-gray-100 rounded cursor-pointer"
                    onClick={() => setSelectedAdId(matchingAd.id)}
                  >
                    <span className="truncate max-w-[120px] inline-block" title={`Ad ID: ${matchingAd.id} (Ad Group ID: ${matchingAd.ad_group_id})`}>
                      <span className="font-medium">Ad:</span> {matchingAd.id} <span className="text-gray-500">(AG: {String(matchingAd.ad_group_id).substring(0, 6)}...)</span>
                    </span>
                  </button>
                );
              }
              return null;
            })()}

            {/* Always show the ad group if available */}
            {term.googleads_ad_group_id ? (
              <button
                className="text-left text-xs px-2 py-1 hover:bg-gray-100 rounded cursor-pointer"
                onClick={() => setSelectedAdGroupId(term.googleads_ad_group_id)}
              >
                <span className="truncate max-w-[120px] inline-block text-muted-foreground" title={`Ad Group ID: ${term.googleads_ad_group_id}`}>
                  <span className="font-medium">Group:</span> {String(term.googleads_ad_group_id).substring(0, 8)}...
                </span>
              </button>
            ) : (
              <span className="text-xs text-muted-foreground px-2 py-1">No ad group data</span>
            )}
          </div>
        </TableCell>

        <TableCell>
          {term.matched_rules.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {term.matched_rules[0].tags.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs px-1 py-0">
                  {tag}
                </Badge>
              ))}
            </div>
          ) : '-'}
        </TableCell>
        <TableCell>
          {term.matched_rules.length > 0 ? (
            <div className="text-xs">
              {term.matched_rules[0].campaign_type || '-'}
            </div>
          ) : '-'}
        </TableCell>
        <TableCell>
          <div className="flex flex-col gap-1">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedKeyword({
                  googleads_ad_group_id: term.googleads_ad_group_id,
                  text: term.search_term || '',
                  keyword: term.search_term,
                  match_type: isExactMatch ? "EXACT" : "BROAD"
                });
                setShowAddModal(true);
              }}
              disabled={term.is_already_negative}
              className={`text-xs h-7 px-2 whitespace-nowrap ${
                term.is_already_negative
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-800'
              }`}
              title={term.is_already_negative ? "Already added as a negative keyword" : "Add as negative keyword"}
            >
              + Add
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  // The main CARD.
  return (
    <Card className='p-6 bg-white border'>
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium">Negative Keywords Analysis</h3>
        </div>
      </div>

      {/* Add Negative Keyword Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium mb-4">
              {isAddingAllTerms 
                ? `Add Selected Negative Keywords (${selectedTerms.size})` 
                : selectedKeyword 
                  ? `Add "${selectedKeyword.keyword}" as Negative Keyword` 
                  : 'Add Negative Keyword'
              }
            </h3>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium">Negative Keyword</label>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="exactMatch"
                      checked={isExactMatch}
                      onChange={(e) => {
                        setIsExactMatch(e.target.checked);
                        
                        // Get all lines from the current input
                        const lines = inputValue.trim().split('\n');
                        const newLines = lines.map(line => {
                          // Parse each line to maintain 'in adGroupId' part
                          const parts = line.split(' in ');
                          if (parts.length === 2) {
                            const term = parts[0].replace(/^\[|\]$/g, '');
                            return e.target.checked ? 
                              `[${term}] in ${parts[1]}` : 
                              `${term} in ${parts[1]}`;
                          }
                          return line; // If not in expected format, return unchanged
                        });
                        setInputValue(newLines.join('\n') + (newLines.length > 0 ? '\n' : ''));
                      }}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <label htmlFor="exactMatch" className="text-sm text-gray-500 ml-2">
                      Exact match
                    </label>
                  </div>
                </div>
                
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="w-full p-2 border rounded-md h-24"
                  placeholder="Enter negative keyword"
                />
              </div>

            </div>

            <div className="flex justify-end space-x-2 mt-2">
              <Button variant="outline" onClick={() => {
                setShowAddModal(false);
                setIsAddingAllTerms(false);
              }}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  processAllNegativeKeywords();
                }}
                disabled={(!inputValue.trim() && !isAddingAllTerms) || isApplyingAll}
              >
                {isApplyingAll ? <Spinner className="mr-2 h-4 w-4" /> : null}
                Add Negative Keywords
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Campaign Averages Section */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-medium">Campaign Averages</h3>
          </div>
          {campaignAverages && (
            <div className="text-xs text-muted-foreground">
              Based on {searchTerms.length} search terms between {formatDate(campaignAverages.startDate)} and {formatDate(campaignAverages.endDate)} inclusive ({campaignAverages.days} total days)
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-8 mb-4 bg-white rounded-md border">
          <Spinner />
          <span className="ml-2">Calculating averages...</span>
        </div>
      ) : campaignAverages ? (
        <div className="rounded-md border mb-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/4">Metric</TableHead>
                <TableHead className="w-[15%] text-right">Average Per Term</TableHead>
                <TableHead className="w-[15%] text-right">Campaign Total</TableHead>
                <TableHead className="w-[15%] text-right">Good Benchmark</TableHead>
                <TableHead className="w-[15%] text-right">Warning Level</TableHead>
                <TableHead className="w-[15%] text-right">Critical Level</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="border-b border-gray-200">
                <TableCell className="font-medium">Impressions</TableCell>
                <TableCell className="text-right">{campaignAverages && Math.round(campaignAverages.avgImpressions || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right">{campaignAverages && campaignAverages.totalImpressions ? campaignAverages.totalImpressions.toLocaleString() : '-'}</TableCell>
                <TableCell className="text-right">-</TableCell>
                <TableCell className="text-right"><span className="text-yellow-600">&gt; {campaignAverages && Math.round((campaignAverages.avgImpressions || 0) * 3).toLocaleString()}</span></TableCell>
                <TableCell className="text-right"><span className="text-red-600">&gt; {campaignAverages && Math.round((campaignAverages.avgImpressions || 0) * 5).toLocaleString()}</span></TableCell>
              </TableRow>
              <TableRow className="border-b-0">
                <TableCell className="font-medium">Clicks</TableCell>
                <TableCell className="text-right">{campaignAverages && Math.round(campaignAverages.avgClicks || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right">{campaignAverages && typeof campaignAverages.totalClicks === 'number' ? campaignAverages.totalClicks.toLocaleString() : '-'}</TableCell>
                <TableCell className="text-right">-</TableCell>
                <TableCell className="text-right"><span className="text-yellow-600">&gt; {campaignAverages && Math.round((campaignAverages.avgClicks || 0) * 2).toLocaleString()}</span></TableCell>
                <TableCell className="text-right"><span className="text-red-600">&gt; {campaignAverages && Math.round((campaignAverages.avgClicks || 0) * 4).toLocaleString()}</span></TableCell>
              </TableRow>
              <TableRow className="border-b-0">
                <TableCell className="font-medium">Clickthrough Rate (CTR)</TableCell>
                <TableCell className="text-right">{campaignAverages ? ((campaignAverages.avgCtr || 0) * 100).toFixed(2) : '0.00'}%</TableCell>
                <TableCell className="text-right">{campaignAverages && campaignAverages.totalImpressions > 0 ? ((campaignAverages.totalClicks / campaignAverages.totalImpressions) * 100).toFixed(2) : '0.00'}%</TableCell>
                <TableCell className="text-right"><span className="text-green-600">&gt; {campaignAverages ? ((campaignAverages.avgCtr || 0) * 100 * 1.2).toFixed(2) : '0.00'}%</span></TableCell>
                <TableCell className="text-right"><span className="text-yellow-600">&lt; {campaignAverages ? ((campaignAverages.avgCtr || 0) * 100 * 0.5).toFixed(2) : '0.00'}%</span></TableCell>
                <TableCell className="text-right"><span className="text-red-600">&lt; {campaignAverages ? ((campaignAverages.avgCtr || 0) * 100 * 0.2).toFixed(2) : '0.00'}%</span></TableCell>
              </TableRow>
              <TableRow className="border-b border-gray-200">
                <TableCell className="font-medium">Cost per Click (CPC)</TableCell>
                <TableCell className="text-right">{currencySymbol}{campaignAverages ? (campaignAverages.avgCpc || 0).toFixed(2) : '0.00'}</TableCell>
                <TableCell className="text-right">{currencySymbol}{campaignAverages && campaignAverages.totalClicks > 0 ? (campaignAverages.totalCost / campaignAverages.totalClicks).toFixed(2) : '-'}</TableCell>
                <TableCell className="text-right"><span className="text-green-600">&lt; {currencySymbol}{campaignAverages ? ((campaignAverages.avgCpc || 0) * 0.8).toFixed(2) : '0.00'}</span></TableCell>
                <TableCell className="text-right"><span className="text-yellow-600">&gt; {currencySymbol}{campaignAverages ? ((campaignAverages.avgCpc || 0) * 1.5).toFixed(2) : '0.00'}</span></TableCell>
                <TableCell className="text-right"><span className="text-red-600">&gt; {currencySymbol}{campaignAverages ? ((campaignAverages.avgCpc || 0) * 2.5).toFixed(2) : '0.00'}</span></TableCell>
              </TableRow>
              <TableRow className="border-b-0">
                <TableCell className="font-medium">Conversions</TableCell>
                <TableCell className="text-right">{campaignAverages && Math.round(campaignAverages.avgConversions || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right">{campaignAverages && typeof campaignAverages.totalConversions === 'number' ? campaignAverages.totalConversions.toLocaleString() : '-'}</TableCell>
                <TableCell className="text-right">-</TableCell>
                <TableCell className="text-right"><span className="text-yellow-600">&gt; {campaignAverages && Math.round((campaignAverages.avgConversions || 0) * 2).toLocaleString()}</span></TableCell>
                <TableCell className="text-right"><span className="text-red-600">&gt; {campaignAverages && Math.round((campaignAverages.avgConversions || 0) * 4).toLocaleString()}</span></TableCell>
              </TableRow>
              <TableRow className="border-b-0">
                <TableCell className="font-medium">Conversion Rate (CVR)</TableCell>
                <TableCell className="text-right">{campaignAverages ? ((campaignAverages.avgCvr || 0) * 100).toFixed(2) : '0.00'}%</TableCell>
                <TableCell className="text-right">{campaignAverages && campaignAverages.totalClicks > 0 ? ((campaignAverages.totalConversions / campaignAverages.totalClicks) * 100).toFixed(2) : '0.00'}%</TableCell>
                <TableCell className="text-right"><span className="text-green-600">&gt; {campaignAverages ? ((campaignAverages.avgCvr || 0) * 100 * 1.2).toFixed(2) : '0.00'}%</span></TableCell>
                <TableCell className="text-right"><span className="text-yellow-600">&lt; {campaignAverages ? ((campaignAverages.avgCvr || 0) * 100 * 0.5).toFixed(2) : '0.00'}%</span></TableCell>
                <TableCell className="text-right"><span className="text-red-600">&lt; {campaignAverages ? ((campaignAverages.avgCvr || 0) * 100 * 0.2).toFixed(2) : '0.00'}%</span></TableCell>
              </TableRow>
              <TableRow className="border-b border-gray-200">
                <TableCell className="font-medium">Cost Per Conversion (CPA)</TableCell>
                <TableCell className="text-right">
                  {campaignAverages && campaignAverages.avgConversions > 0
                    ? `${currencySymbol}${(campaignAverages.avgCost / campaignAverages.avgConversions).toFixed(2)}`
                    : '-'}
                </TableCell>
                <TableCell className="text-right">
                  {campaignAverages && campaignAverages.totalConversions > 0
                    ? `${currencySymbol}${(campaignAverages.totalCost / campaignAverages.totalConversions).toFixed(2)}`
                    : '-'}
                </TableCell>
                <TableCell className="text-right">
                  {campaignAverages && campaignAverages.avgConversions > 0 ? (
                    <span className="text-green-600">&lt; {currencySymbol}{(campaignAverages.avgCost / campaignAverages.avgConversions * 0.8).toFixed(2)}</span>
                  ) : '-'}
                </TableCell>
                <TableCell className="text-right">
                  {campaignAverages && campaignAverages.avgConversions > 0 ? (
                    <span className="text-yellow-600">&gt; {currencySymbol}{(campaignAverages.avgCost / campaignAverages.avgConversions * 1.5).toFixed(2)}</span>
                  ) : '-'}
                </TableCell>
                <TableCell className="text-right">
                  {campaignAverages && campaignAverages.avgConversions > 0 ? (
                    <span className="text-red-600">&gt; {currencySymbol}{(campaignAverages.avgCost / campaignAverages.avgConversions * 3).toFixed(2)}</span>
                  ) : '-'}
                </TableCell>
              </TableRow>
              <TableRow className="border-b-0">
                <TableCell className="font-medium">Total Cost</TableCell>
                <TableCell className="text-right">{currencySymbol}{campaignAverages ? (campaignAverages.avgCost || 0).toFixed(2) : '0.00'}</TableCell>
                <TableCell className="text-right">{currencySymbol}{campaignAverages ? (campaignAverages.totalCost || 0).toFixed(2) : '0.00'}</TableCell>
                <TableCell className="text-right">-</TableCell>
                <TableCell className="text-right"><span className="text-yellow-600">&gt; {currencySymbol}{campaignAverages ? ((campaignAverages.avgCost || 0) * 2).toFixed(2) : '0.00'}</span></TableCell>
                <TableCell className="text-right"><span className="text-red-600">&gt; {currencySymbol}{campaignAverages ? ((campaignAverages.avgCost || 0) * 4).toFixed(2) : '0.00'}</span></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center p-4 bg-muted rounded-md mb-6">
          <p className="text-sm text-muted-foreground">No campaign data available</p>
        </div>
      )}

      {/* Campaign Ads Table */}
      {/* Ads and Ad Groups Header - Same style as Search Terms header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-medium">Ads and Ad Groups</h3>
            {campaignAds.length > 0 && (
              <Badge variant="outline">{campaignAds.length} total</Badge>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        // Loading state
        <div className="flex items-center justify-center p-8 mb-4 bg-white rounded-md border">
          <Spinner />
          <span className="ml-2">Loading campaign ads...</span>
        </div>
      ) : campaignAds.length > 0 ? (
        // Ads found - display in a table
        <div className="rounded-md border mb-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad Group</TableHead>
                <TableHead>Ad URL</TableHead>
                <TableHead>Negative Keywords</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(campaignAds as Array<Ad & { negative_keywords?: NegativeKeyword[]; googleads_ad_group_id?: string }>).map((ad) => (
                <TableRow key={ad.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center">
                      <div className="flex flex-col">
                        <button 
                          className="text-left text-blue-600 hover:underline truncate max-w-[150px] mr-2"
                          title={`Ad ID: ${ad.id}${ad.funnel_stage ? ` | Funnel: ${ad.funnel_stage}` : ''}`}
                          onClick={() => {
                            // Filter search terms by ad_group_id when ad is clicked
                            if (ad.googleads_ad_group_id) {
                              setSelectedAdId(ad.id);
                            }
                          }}
                        >
                          {ad.googleads_ad_group_id}
                        </button>
                        {ad.funnel_stage && (
                          <span className="text-sm text-gray-500">
                            {ad.funnel_stage.replace('_funnel', '')}
                          </span>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => window.open(`/campaigns/google/${campaignSlug}/ads/${ad.slug}`, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="truncate max-w-[180px] inline-block" title={ad.url}>
                      {ad.url}
                    </span>
                  </TableCell>
                  <TableCell>
                    {ad.negative_keywords && ad.negative_keywords.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {ad.negative_keywords.map((keyword: NegativeKeyword, index: number) => (
                          <Badge 
                            key={`${keyword.googleads_ad_group_id}-${keyword.keyword || index}`} 
                            className="py-1 bg-red-50 text-red-600 border-red-200 cursor-pointer"
                            onClick={async () => {
                              try {
                                // Call the API to pause/remove the negative keyword
                                await pauseNegativeKeywords(numericCampaignId as number, [
                                  {
                                    googleads_ad_group_id: keyword.googleads_ad_group_id,
                                    keyword: keyword.keyword || keyword.text || ''
                                  }
                                ]);
                                
                                // Update React Query cache data
                                queryClient.setQueryData(['negative-keywords-report', campaignSlug, 7], (oldData: NegativeKeywordsReportData | undefined) => {
                                  if (!oldData) return oldData;

                                  return {
                                    ...oldData,
                                    search_terms: oldData.search_terms.map((term: SearchTerm) => {
                                      if (term.googleads_ad_group_id === keyword.googleads_ad_group_id && 
                                          term.search_term === keyword.keyword) {
                                        return { ...term, is_already_negative: false };
                                      }
                                      return term;
                                    }),
                                    ads: oldData.ads.map((ad) => {
                                      if (ad.googleads_ad_group_id === keyword.googleads_ad_group_id) {
                                        const filteredKeywords = (ad.negative_keywords || []).filter((nk: NegativeKeyword) =>
                                          !(nk.googleads_ad_group_id === keyword.googleads_ad_group_id &&
                                            nk.keyword === keyword.keyword)
                                        );
                                        return { ...ad, negative_keywords: filteredKeywords };
                                      }
                                      return ad;
                                    })
                                  };
                                });
                                
                                toast({
                                  title: "Negative Keyword Removed",
                                  description: `Successfully removed "${keyword.keyword}" from negative keywords`,
                                  variant: "default"
                                });
                              } catch (error) {
                                console.error('Error removing negative keyword:', error);
                                toast({
                                  title: "Error",
                                  description: error instanceof Error ? error.message : 'Failed to remove negative keyword',
                                  variant: "destructive"
                                });
                                
                                // Fall back to a full refresh in case of error
                                // Data will automatically refresh via React Query
                              }
                            }}
                          >
                            <span className="mr-1 text-red-600">-</span>
                            {keyword.match_type === 'EXACT' ? `[${keyword.keyword}]` : keyword.keyword}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">None</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        // No ads found
        <div className="text-center p-8 bg-muted rounded-md mb-4">
          <p className="text-lg font-medium mb-2">No Ads Found</p>
          <p className="text-sm text-muted-foreground">
            No ads were found in this campaign.
          </p>
        </div>
      )}
      
      
      <div className="mt-4">
        {/* Filter indicators */}
        {(selectedAdId !== null || selectedAdGroupId !== null) && (
          <div className="bg-muted p-2 mb-4 rounded-md flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm">Active filters:</span>
              {selectedAdId !== null && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Ad {selectedAdId}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-1"
                    onClick={() => setSelectedAdId(null)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {selectedAdGroupId !== null && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  Ad Group {selectedAdGroupId}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-1"
                    onClick={() => setSelectedAdGroupId(null)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => {
                setSelectedAdId(null);
                setSelectedAdGroupId(null);
              }}
            >
              Clear All Filters
            </Button>
          </div>
        )}

        {/* Info about candidates + algorithm selection */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-medium">Search Terms</h3>
              <Badge variant="outline">{searchTerms.length} total</Badge>
            </div>
            
          </div>
        </div>

        {/* Inline version of renderTermsTable */}
        {(() => {
          if (isLoading) {
            return (
              <div className="flex items-center justify-center p-8">
                <Spinner />
                <span className="ml-2">Loading search terms data...</span>
              </div>
            );
          }

          if (error) {
            return (
              <div className="p-4 bg-destructive/10 text-destructive rounded-md">
                <p className="font-medium">Error loading data</p>
                <p className="text-sm">{error}</p>
              </div>
            );
          }

          if (!displayTerms.length) {
            return (
              <div className="text-center p-8 bg-muted rounded-md">
                <p className="text-lg font-medium mb-2">No search terms data available</p>
                <p className="text-sm text-muted-foreground">
                  No negative keyword candidates were found matching the current rules.
                </p>
              </div>
            );
          }

          const virtualItems = rowVirtualizer.getVirtualItems();
          const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
          const paddingBottom = virtualItems.length > 0
            ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
            : 0;

          return (
            <div ref={termsScrollRef} className="rounded-md border max-h-[600px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-white">
                  <TableRow>
                    <TableHead className="border-r border-gray-200">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectAllChecked}
                          onChange={(e) => toggleAllTerms(e.target.checked)}
                          className="h-4 w-4 mr-2 text-blue-600 border-gray-300 rounded"
                          style={{ width: '16px', height: '16px', minWidth: '16px', minHeight: '16px' }}
                          title="Select all search terms"
                        />
                        Search Term
                      </div>
                    </TableHead>
                    <TableHead className="w-[100px] text-right">Impr.</TableHead>
                    <TableHead className="w-[80px] text-right">Clicks</TableHead>
                    <TableHead className="w-[80px] text-right">Conv.</TableHead>
                    <TableHead className="w-[80px] text-right border-l border-gray-200">Cost</TableHead>
                    <TableHead className="w-[80px] text-right">CPC</TableHead>
                    <TableHead className="w-[80px] text-right">CTR</TableHead>
                    <TableHead className="w-[80px] text-right">CVR</TableHead>
                    <TableHead className="w-[150px] border-l border-gray-200">Reason</TableHead>
                    <TableHead className="w-[100px]">Type</TableHead>
                    <TableHead className="w-[100px]">
                      {handleApplyAllCandidates ? (
                        <Button
                          onClick={handleApplyAllCandidates}
                          disabled={isApplyingAll || selectedTerms.size === 0 || campaignAds.length === 0}
                          className="text-xs h-7 px-2 text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-800 border border-red-200 whitespace-nowrap"
                        >
                          {isApplyingAll ? (
                            <>
                              <Spinner className="mr-2 h-4 w-4" />
                              Adding...
                            </>
                          ) : (
                            <>
Add selected
                            </>
                          )}
                        </Button>
                      ) : (
                        "Actions"
                      )}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paddingTop > 0 && (
                    <tr aria-hidden="true">
                      <td colSpan={12} style={{ height: paddingTop, padding: 0, border: 0 }} />
                    </tr>
                  )}
                  {rowVirtualizer.getVirtualItems().map((virtualRow) =>
                    renderTermRow(displayTerms[virtualRow.index], virtualRow.index)
                  )}
                  {paddingBottom > 0 && (
                    <tr aria-hidden="true">
                      <td colSpan={12} style={{ height: paddingBottom, padding: 0, border: 0 }} />
                    </tr>
                  )}
                </TableBody>
              </Table>
            </div>
          );
        })()}
      </div>
    </Card>
  );
};
